import { signal, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MasterViewModel } from 'src/app/base-classes/master-view-model';
import { ElementCaptureService, IdentifiedStep, CapturedPage } from '../services/element-capture.service';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { StepsService } from '../../steps/services/steps.service';
import { CreateStepRequest } from '../../steps/models';
import { ToastService } from 'src/app/toast/toast.service';
import { ExecutionResultService } from 'src/app/services/execution-result.service';
import { StepPlaceholderService } from 'src/app/services/step-placeholder.service';
import { AiStepGeneratorService } from 'src/app/services/ai-step-generator.service';
import { LiveTestExecutorComponent } from '../../live-test-executor/live-test-executor.component';
import { environment } from 'src/environments/environment';

export class ElementCaptureViewModel extends MasterViewModel<any> {
  // UI State
  splitPx = 440;
  dragging = false;
  url = '';
  log = 'Ready.';
  
  // Data
  pages = signal<CapturedPage[]>([]);
  identifiedSteps = signal<IdentifiedStep[]>([]);
  
  // AI Generator
  aiScenarioInput = '';
  aiGeneratedSteps = signal<string[]>([]);
  isAIGenerating = signal(false);
  aiError = signal('');
  
  // Generation state
  isGenerating = signal(false);
  
  // Context
  featureId: string = '';
  functionalAreaId: string = '';
  scenarioId: string = '';
  testSteps: string = '';
  deleteOldSteps: boolean = false;
  oldStepSource: string | null = null;
  
  // Component reference
  liveTestExecutor?: LiveTestExecutorComponent;
  
  // Polling
  private pollingInterval: any;
  private readonly POLL_INTERVAL_MS = 2000;
  private shouldStopPolling = false;

  constructor(
    http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private elementCaptureService: ElementCaptureService,
    private scenarioService: ScenarioService,
    private stepsService: StepsService,
    private toastService: ToastService,
    private executionResultService: ExecutionResultService,
    private stepPlaceholderService: StepPlaceholderService,
    private aiStepGenerator: AiStepGeneratorService
  ) {
    const baseUrl = `${environment.apiUrl}/element-capture`;
    const moduleName = 'Element Capture';
    super(http, baseUrl, moduleName);
  }

  async init(liveTestExecutor: LiveTestExecutorComponent) {
    this.liveTestExecutor = liveTestExecutor;
    
    this.route.params.subscribe(async params => {
      this.functionalAreaId = params['functionalAreaId'] || this.functionalAreaId || '';
      this.scenarioId = params['scenarioId'] || this.scenarioId || '';

      if (this.functionalAreaId && this.scenarioId) {
        this.executionResultService.setExecutionContext(this.functionalAreaId, this.scenarioId);
        this.getTestSteps();
      }

      await this.getScenarioDetail();

      const captureContext = { functionalAreaId: this.functionalAreaId, scenarioId: this.scenarioId };
      await this.elementCaptureService.setCaptureMode(true, {
        ...captureContext,
        initialUrl: this.url
      });
      this.elementCaptureService.setSplitPosition(this.splitPx);
    });

    this.route.queryParams.subscribe(params => {
      this.deleteOldSteps = params['deleteOldSteps'] === 'true';
      const sourceParam = params['oldStepSource'];
      this.oldStepSource = (sourceParam && sourceParam !== 'null') ? sourceParam : null;
    });
  }

  destroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  // Navigation Methods
  startDrag(evt: MouseEvent) {
    this.dragging = true;
    evt.preventDefault();
  }

  onMove(evt: MouseEvent) {
    if (!this.dragging) return;
    const next = Math.max(260, evt.clientX);
    this.splitPx = next;
    this.elementCaptureService.setSplitPosition(this.splitPx);
  }

  onUp() {
    this.dragging = false;
  }

  go() {
    if (this.url) {
      this.shouldStopPolling = false;
      this.elementCaptureService.navigate(this.url);
    }
  }

  back() {
    this.elementCaptureService.back();
  }

  forward() {
    this.elementCaptureService.forward();
  }

  reload() {
    this.elementCaptureService.reload();
  }

  devtools() {
    this.elementCaptureService.devtools();
  }

  extract() {
    this.elementCaptureService.extractElements();
  }

  cancel() {
    const clientId = sessionStorage.getItem('scenario_clientId');
    const testTypeId = sessionStorage.getItem('scenario_testTypeId');
    const functionalAreaId = sessionStorage.getItem('scenario_functionalAreaId');
    
    const queryParams: any = {};
    
    if (clientId) {
      queryParams.clientId = clientId;
    }

    if (testTypeId) {
      queryParams.testTypeId = testTypeId;
    }
    
    if (functionalAreaId) {
      queryParams.functionalAreaId = functionalAreaId;
    }
    
    this.router.navigate(['/scenario'], { queryParams });
  }

  // Save Methods
  async saveStepScenario() {
    try {
      if (!this.scenarioId) {
        this.toastService.error('Scenario is missing. Cannot save steps.');
        return;
      }
      await this.saveSteps();
      if (this.url && this.scenarioId) {
        await this.updateScenarioUrl();
      }

      this.toastService.success('Scenario saved successfully with steps.');
      this.router.navigate(['/scenario'], {
        queryParams: { functionalAreaId: this.functionalAreaId }
      });
    } catch (error) {
      this.toastService.error('Failed to save scenario steps. Please try again.');
    }
  }

  private async saveSteps(): Promise<void> {
    try {
      const testScenarioSteps = await this.getTestScenarioSteps();
      const stepLines = testScenarioSteps
        .split('\n')
        .map((line: any) => line.trim())
        .filter((line: any) => line && !line.startsWith('#'));

      if (stepLines.length === 0) {
        throw new Error('No valid steps found to save');
      }

      const stepsArray: CreateStepRequest[] = stepLines.map((stepDescription: string, index: number) => {
        const parsed = this.stepPlaceholderService.parseStepDescription(stepDescription);
        
        return {
          scenarioId: this.scenarioId,
          stepId: index + 1,
          stepDescription: stepDescription,
          placeholder: parsed.placeholder || stepDescription,
          additionalInfo: JSON.stringify({
            type: 'step',
            ...parsed.additionalInfo
          }),
          isRecording: false 
        };
      });

      const bulkStepPayload = {
        scenarioId: this.scenarioId,
        steps: stepsArray,
        deleteOldSteps: this.deleteOldSteps
      };
      await firstValueFrom(this.stepsService.saveBulkStep(bulkStepPayload));

    } catch (error) {
      console.error('Failed to save steps:', error);
      throw new Error('Failed to save steps to database');
    }
  }

  private async getTestScenarioSteps(): Promise<any> {
    try {
      if (this.liveTestExecutor && this.liveTestExecutor.currentStepInput) {
        if (Array.isArray(this.liveTestExecutor.currentStepInput)) {
          const testScenario = this.liveTestExecutor.currentStepInput
            .filter(step => step.isActive)
            .sort((a, b) => a.stepId - b.stepId)
            .map(step => step.stepDescription)
            .join('\n')
            .trim();

          if (testScenario) {
            return testScenario;
          }
        } else if (typeof this.liveTestExecutor.currentStepInput === 'string') {
          const testScenario = this.liveTestExecutor.currentStepInput.trim();
          if (testScenario) {
            return testScenario;
          }
        }
      }
    } catch (error) {
    }
  }

  private async updateScenarioUrl(): Promise<void> {
    try {
      const updateData = { url: this.url };
      await firstValueFrom(this.scenarioService.updateScenario(this.scenarioId, updateData));
    } catch (error) {
      console.error('Failed to update scenario URL:', error);
    }
  }

  public async getScenarioDetail(): Promise<any> {
    this.scenarioService.getScenarioById(this.scenarioId).subscribe({
      next: (res: any) => {
        if (res) {
          this.url = res.url || '';
        }
      },
      error: (error) => { }
    });
  }

  public getTestSteps(): void {
    this.stepsService.getStepsByScenario(this.scenarioId).subscribe({
      next: (res: any) => {
        if (res.isSuccess && this.liveTestExecutor) {
          const stepObjects = res.data.map((step: any) => ({
            isActive: step.isActive !== undefined ? step.isActive : true,
            id: step.id || '',
            scenarioId: step.scenarioId || this.scenarioId,
            stepId: step.stepId,
            stepDescription: step.stepDescription,
            placeholder: step.placeholder || '',
            additionalInfo: step.additionalInfo || '{"type":"step"}'
          }));

          this.liveTestExecutor.currentStepInput = stepObjects;
        }
      },
      error: (error) => {
      }
    });
  }

  // Test Step Generation Methods
  async generateTestSteps(): Promise<void> {
    this.isGenerating.set(true);
    
    try {
      const [elementsResponse, textResponse] = await Promise.all([
        this.elementCaptureService.getElementMap(),
        this.elementCaptureService.getTextElements()
      ]);
      
      if (elementsResponse?.success && elementsResponse.elementMap && elementsResponse.elementMap.length > 0) {
        const steps = this.transformElementsToSteps(elementsResponse.elementMap);
        
        if (textResponse?.success && textResponse.textElements && textResponse.textElements.length > 0) {
          this.addTextVerificationSteps(steps, textResponse.textElements);
        }
        
        this.identifiedSteps.set(steps);
        
        const verificationCount = this.getVerificationSteps().length;
        const actionCount = this.getActionSteps().length;
        
        this.toastService.success(
          `Generated ${steps.length} test steps (${actionCount} actions, ${verificationCount} verifications)`
        );
      } else {
        const errorMsg = elementsResponse?.error || 'No elements found on the page. Please navigate to a page first.';
        this.toastService.error(errorMsg);
      }
    } catch (error) {
      this.toastService.error('Failed to generate test steps. Please ensure the page is loaded.');
    } finally {
      this.isGenerating.set(false);
    }
  }

  private transformElementsToSteps(elementMap: any[]): IdentifiedStep[] {
    const steps: IdentifiedStep[] = [];
    
    elementMap.forEach(element => {
      const elementType = element.type.toLowerCase();
      const bestIdentifier = this.getBestIdentifier(element.identifiers, elementType);
      const cleanIdentifier = this.cleanIdentifierName(bestIdentifier);
      
      if (elementType === 'textbox' || elementType === 'text' || 
          elementType === 'email' || elementType === 'password' || 
          elementType === 'textarea') {
        steps.push({
          stepDescription: `Enter {{value}} in ${cleanIdentifier}`,
          elementType: 'input',
          identifiers: element.identifiers,
          isAdded: false
        });
      }
      else if (elementType === 'button') {
        const buttonText = cleanIdentifier.toLowerCase().includes('button') 
          ? cleanIdentifier 
          : `${cleanIdentifier} button`;
        
        steps.push({
          stepDescription: `Click ${buttonText}`,
          elementType: 'button',
          identifiers: element.identifiers,
          isAdded: false
        });
      }
      else if (elementType === 'dropdown' || elementType === 'select') {
        steps.push({
          stepDescription: `Enter {{value}} in ${cleanIdentifier}`,
          elementType: 'dropdown',
          identifiers: element.identifiers,
          isAdded: false
        });
        steps.push({
          stepDescription: `Press Enter Key`,
          elementType: 'action',
          identifiers: ['keyboard'],
          isAdded: false
        });
        steps.push({
          stepDescription: `Wait 2 seconds`,
          elementType: 'wait',
          identifiers: ['time'],
          isAdded: false
        });
        steps.push({
          stepDescription: `Select {{option}} option`,
          elementType: 'select',
          identifiers: element.identifiers,
          isAdded: false
        });
        steps.push({
          stepDescription: `Click OK button`,
          elementType: 'button',
          identifiers: ['OK'],
          isAdded: false
        });
      }
      else if (elementType === 'link' || elementType === 'a') {
        steps.push({
          stepDescription: `Click ${cleanIdentifier} link`,
          elementType: 'link',
          identifiers: element.identifiers,
          isAdded: false
        });
      }
      else if (elementType === 'checkbox') {
        steps.push({
          stepDescription: `Check ${cleanIdentifier} checkbox`,
          elementType: 'checkbox',
          identifiers: element.identifiers,
          isAdded: false
        });
      }
      else if (elementType === 'radio') {
        steps.push({
          stepDescription: `Select ${cleanIdentifier} radio`,
          elementType: 'radio',
          identifiers: element.identifiers,
          isAdded: false
        });
      }
      else {
        if (element.identifiers && element.identifiers.length > 0) {
          steps.push({
            stepDescription: `Click ${cleanIdentifier}`,
            elementType: elementType,
            identifiers: element.identifiers,
            isAdded: false
          });
        }
      }
    });
    
    return steps;
  }

  private addTextVerificationSteps(steps: IdentifiedStep[], textElements: string[]): void {
    const validTexts = Array.from(new Set(textElements))
      .filter(text => {
        const isValid = text && text.length >= 3 && text.length <= 100;
        const hasLetters = /[a-zA-Z]/.test(text);
        const notPureSymbols = !/^[\d\s\-_]+$/.test(text);
        const isDescriptive = /^[A-Z]/.test(text) || /\s/.test(text) || text.length > 5;
        
        return isValid && hasLetters && notPureSymbols && isDescriptive;
      });
    
    const scoredTexts = validTexts.map(text => {
      let score = 0;
      
      if (text.length >= 10 && text.length <= 30) score += 100;
      else if (text.length >= 5 && text.length <= 50) score += 50;
      
      if (text.length > 70) score -= 50;
      if (!/[…\/:\.,]/.test(text)) score += 30;
      if (/^[A-Z][a-z]+ [A-Z]/.test(text)) score += 40;
      if (/opens new window|month\(s\) ago|\d+:\d+:\d+|©|Proprietary/i.test(text)) score -= 100;
      if (/^(Hi There|Welcome|Dashboard|Home|Awaiting|Your|Let's|Focus|Top Apps)/i.test(text)) score += 80;
      
      return { text, score };
    });
    
    const uniqueTexts = scoredTexts
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.text.length - b.text.length;
      })
      .slice(0, 10)
      .map(item => item.text);
    
    uniqueTexts.forEach(text => {
      steps.push({
        stepDescription: `Verify page contains ${text}`,
        elementType: 'verification',
        identifiers: [text],
        isAdded: false
      });
    });
  }

  private cleanIdentifierName(identifier: string): string {
    const cleanedName = identifier
      .replace(/\s+(textbox|field|input|button|dropdown|select)$/i, '')
      .trim();
    
    return cleanedName || identifier;
  }

  private getBestIdentifier(identifiers: string[], elementType: string = ''): string {
    if (!identifiers || identifiers.length === 0) {
      return 'element';
    }

    const validIdentifiers = identifiers.filter(id => 
      id && 
      id.trim().length > 0 && 
      id.length < 50 && 
      id.length > 1
    );

    if (validIdentifiers.length === 0) {
      return identifiers[0] || 'element';
    }

    if (elementType === 'button' || elementType === 'link') {
      const visibleText = validIdentifiers.filter(id => {
        const looksLikeText = /^[A-Z]/.test(id) || /\s/.test(id);
        const notTechnicalId = !id.includes('-') && !id.includes('_') && !/([a-z])([A-Z])/.test(id);
        
        return looksLikeText && notTechnicalId;
      });

      if (visibleText.length > 0) {
        return visibleText.sort((a, b) => a.length - b.length)[0];
      }
    }

    const descriptiveOnes = validIdentifiers.filter(id => {
      const hasProperCase = /^[A-Z]/.test(id);
      const hasSpaces = /\s/.test(id);
      const reasonableLength = id.length > 2 && id.length < 30;
      
      return (hasProperCase || hasSpaces) && reasonableLength;
    });

    if (descriptiveOnes.length > 0) {
      return descriptiveOnes.sort((a, b) => a.length - b.length)[0];
    }

    const nonTechnical = validIdentifiers.filter(id => 
      !id.includes('-') && !id.includes('_')
    );

    if (nonTechnical.length > 0) {
      return nonTechnical[0];
    }

    return validIdentifiers[0];
  }

  addStepToScenario(step: IdentifiedStep): void {
    if (step.isAdded) {
      this.toastService.info('This step is already in your scenario');
      return;
    }

    if (this.liveTestExecutor) {
      let currentSteps = '';
      
      if (Array.isArray(this.liveTestExecutor.currentStepInput)) {
        currentSteps = this.liveTestExecutor.currentStepInput
          .filter(s => s.isActive)
          .sort((a, b) => a.stepId - b.stepId)
          .map(s => s.stepDescription)
          .join('\n');
      } else {
        currentSteps = this.liveTestExecutor.currentStepInput || '';
      }
      
      const newSteps = currentSteps && currentSteps.trim()
        ? `${currentSteps}\n${step.stepDescription}`
        : step.stepDescription;
      
      this.liveTestExecutor.currentStepInput = newSteps;
      
      step.isAdded = true;
      
      this.toastService.success(`✓ Step added! Scroll up to see it in the Test Execution section.`);
    } else {
      this.toastService.error('Test executor not ready');
    }
  }

  hasAddedSteps(): boolean {
    return this.identifiedSteps().some(step => step.isAdded);
  }

  getActionSteps(): IdentifiedStep[] {
    return this.identifiedSteps().filter(step => step.elementType !== 'verification');
  }

  getVerificationSteps(): IdentifiedStep[] {
    return this.identifiedSteps().filter(step => step.elementType === 'verification');
  }

  // AI Step Generator Methods
  async generateWithAI(): Promise<void> {
    if (!this.aiScenarioInput || this.aiScenarioInput.trim().length === 0) {
      this.toastService.error('Please enter a test scenario description');
      return;
    }

    this.isAIGenerating.set(true);
    this.aiError.set('');
    this.aiGeneratedSteps.set([]);

    try {
      const elementMapResponse = await this.elementCaptureService.getElementMap();
      const textElementsResponse = await this.elementCaptureService.getTextElements();

      if (!elementMapResponse?.success) {
        throw new Error('Failed to get page elements. Make sure a page is loaded.');
      }

      const request = {
        scenario: this.aiScenarioInput.trim(),
        elementMap: elementMapResponse.elements || [],
        textElements: textElementsResponse?.textElements || [],
        url: this.url
      };

      const response = await this.aiStepGenerator.generateSteps(request);

      if (response.success && response.steps.length > 0) {
        this.aiGeneratedSteps.set(response.steps);
        this.toastService.success(`✨ Generated ${response.steps.length} test steps!`);
      } else {
        const hasElements = (elementMapResponse.elements || []).length > 0;
        
        if (!hasElements) {
          this.aiError.set('⚠️ No interactive elements found on this page. Please navigate to a page with input fields, buttons, or links that match your scenario.');
        } else {
          this.aiError.set(`❌ No steps were generated. The current page may not have the elements needed for "${this.aiScenarioInput}". Try:\n\n1. Navigate to the correct page first\n2. Use more specific element names\n3. Rephrase your scenario`);
        }
        
        this.toastService.error('No steps generated - see details below');
      }
    } catch (error: any) {
      this.aiError.set(error.message || 'Failed to generate steps');
      this.toastService.error(this.aiError());
    } finally {
      this.isAIGenerating.set(false);
    }
  }

  addAIStepToScenario(step: string, index: number): void {
    if (!this.liveTestExecutor) {
      this.toastService.error('Test executor not ready');
      return;
    }

    let currentSteps = '';
    
    if (Array.isArray(this.liveTestExecutor.currentStepInput)) {
      currentSteps = this.liveTestExecutor.currentStepInput
        .filter(s => s.isActive)
        .sort((a, b) => a.stepId - b.stepId)
        .map(s => s.stepDescription)
        .join('\n');
    } else {
      currentSteps = this.liveTestExecutor.currentStepInput || '';
    }
    
    const newSteps = currentSteps && currentSteps.trim()
      ? `${currentSteps}\n${step}`
      : step;
    
    this.liveTestExecutor.currentStepInput = newSteps;
    
    this.toastService.success(`✓ Step added to test scenario!`);
  }

  addAllAISteps(): void {
    if (!this.liveTestExecutor) {
      this.toastService.error('Test executor not ready');
      return;
    }

    if (this.aiGeneratedSteps().length === 0) {
      this.toastService.error('No steps to add');
      return;
    }

    let currentSteps = '';
    
    if (Array.isArray(this.liveTestExecutor.currentStepInput)) {
      currentSteps = this.liveTestExecutor.currentStepInput
        .filter(s => s.isActive)
        .sort((a, b) => a.stepId - b.stepId)
        .map(s => s.stepDescription)
        .join('\n');
    } else {
      currentSteps = this.liveTestExecutor.currentStepInput || '';
    }
    
    const allAISteps = this.aiGeneratedSteps().join('\n');
    const newSteps = currentSteps && currentSteps.trim()
      ? `${currentSteps}\n${allAISteps}`
      : allAISteps;
    
    this.liveTestExecutor.currentStepInput = newSteps;
    
    this.toastService.success(`✓ Added ${this.aiGeneratedSteps().length} steps to test scenario!`);
  }

  clearAISteps(): void {
    this.aiGeneratedSteps.set([]);
    this.aiScenarioInput = '';
    this.aiError.set('');
  }
}
